import React from 'react';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Video, Image } from 'lucide-react';
import { PX_PER_SEC, VIDEO_H, SPEED_RAMPS, getEffDur } from './constants';
import { SpeedRampCurve } from './SpeedRampCurve';

export const SortableClip = ({ track, idx, isSelected, onClick, isOver }) => {
  const effDur = getEffDur(track);
  const clipWidth = Math.max(effDur * PX_PER_SEC, 40);
  const isVid = track.type === 'video';
  const speed = track.speed_factor || 1;
  const ramp = track.speed_ramp;
  const hasFi = (track.fade_in || 0) > 0;
  const hasFo = (track.fade_out || 0) > 0;
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: `track-${track.asset_id}-${idx}`,
    data: { type: 'timeline-clip', index: idx },
  });
  const style = { width: clipWidth, height: VIDEO_H, transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.4 : 1 };

  const hasRamp = ramp && ramp !== 'none';

  return (
    <div
      ref={setNodeRef}
      className={`relative flex-shrink-0 rounded overflow-hidden cursor-pointer border-2 transition-all ${
        isOver ? 'border-green-400 shadow-green-400/30 shadow-md' :
        isSelected ? 'border-purple-500 shadow-purple-500/20 shadow-md' :
        'border-transparent hover:border-border/50'
      }`}
      style={style} onClick={onClick}
      data-testid={`timeline-track-${idx}`}
      {...attributes} {...listeners}
    >
      {isOver && (
        <div className="absolute inset-0 bg-green-400/20 z-20 flex items-center justify-center">
          <span className="text-[9px] font-bold text-green-300 bg-black/60 px-1.5 py-0.5 rounded">Replace</span>
        </div>
      )}
      {track.source_url ? (
        <img src={track.source_url} alt="" className="absolute inset-0 w-full h-full object-cover" />
      ) : (
        <div className={`absolute inset-0 flex items-center justify-center ${isVid ? 'bg-blue-900/40' : 'bg-purple-900/30'}`}>
          <span className="text-[8px] text-white/50 truncate px-1">{track.label || 'Empty'}</span>
        </div>
      )}
      <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent" />
      {hasFi && <div className="absolute inset-y-0 left-0 pointer-events-none" style={{ width: Math.min(clipWidth * 0.4, (track.fade_in / effDur) * clipWidth), background: 'linear-gradient(to right, rgba(0,0,0,0.6), transparent)' }} />}
      {hasFo && <div className="absolute inset-y-0 right-0 pointer-events-none" style={{ width: Math.min(clipWidth * 0.4, (track.fade_out / effDur) * clipWidth), background: 'linear-gradient(to left, rgba(0,0,0,0.6), transparent)' }} />}

      {/* Speed ramp curve overlay */}
      {hasRamp && (
        <div className="absolute bottom-0 left-0 right-0 pointer-events-none opacity-80">
          <SpeedRampCurve
            rampId={ramp}
            width={clipWidth}
            height={Math.min(22, VIDEO_H * 0.45)}
            active
            customPoints={ramp === 'custom' ? track.speed_ramp_points : null}
          />
        </div>
      )}

      <div className="absolute inset-0 flex flex-col justify-between p-1">
        <div className="flex items-center gap-0.5">
          {isVid ? <Video className="h-2.5 w-2.5 text-blue-300" /> : <Image className="h-2.5 w-2.5 text-purple-300" />}
          {!hasRamp && speed !== 1 && <span className="text-[7px] font-bold text-amber-300 bg-black/50 px-0.5 rounded">{speed}x</span>}
          {hasRamp && ramp !== 'custom' && <span className="text-[7px] font-bold bg-black/50 px-0.5 rounded" style={{ color: SPEED_RAMPS[ramp]?.color }}>{SPEED_RAMPS[ramp]?.name}</span>}
          {ramp === 'custom' && <span className="text-[7px] font-bold bg-black/50 px-0.5 rounded text-purple-400">Custom</span>}
        </div>
        <span className="text-[9px] font-semibold text-white">{effDur.toFixed(1)}s</span>
      </div>
      {isSelected && <div className="absolute top-0 left-0 right-0 h-0.5 bg-purple-500" />}
    </div>
  );
};
